import React, { useState, ChangeEvent, useEffect } from 'react';
import { ModalWithButtons } from '../../ModalWithButtons/ModalWithButtons.component';
import { Grid, TextField, FormHelperTextProps, Typography, FormControlLabel } from '@material-ui/core';
import { LeavePrompt } from '../../Prompt/LeavePrompt/LeavePrompt.component';
import { useStyles } from './SpecialCases.styles';
import { t } from 'i18next';
import { useLayoutContext } from '../../../Context/Layout.context';
import { SpecialCase, SpecialCaseResult, WorkMode } from './SpecialCases.types';
import { CheckboxOne } from '../../CheckboxOne/CheckboxOne.component';
import { ValidationRow } from '../../ValidationRow/ValidationRow.components';
import {
  createSpecialCasesDefinition,
  updateSpecialCasesDefinition,
} from '../../../services/admin/settings/systemSettings.service';

const TITLE_MAX_LENGTH = 256;
const DESCRIPTION_MAX_LENGTH = 1024;

interface EditSpecialCaseProps {
  data: SpecialCase;
  title: string;
  workMode: WorkMode;
  onCancel: () => void;
  onClose: () => void;
}

export const EditSpecialCase: React.FC<EditSpecialCaseProps> = ({ data, title, workMode, onCancel, onClose }) => {
  const classes = useStyles();

  const { showLoader, openAlert } = useLayoutContext();

  const [isLeavePromptOpen, setIsLeavePromptOpen] = useState<boolean>(false);
  const [specialCaseTitle, setSpecialCaseTitle] = useState<string>(data.title ?? '');
  const [specialCaseDescription, setSpecialCaseDescription] = useState<string>(data.description ?? '');
  const [domesticCoordinator, setDomesticCoordinator] = useState<boolean>(data.domesticCoordinator ?? false);
  const [examTaskGroupCoordinator, setExamTaskGroupCoordinator] = useState<boolean>(
    data.examTaskGroupCoordinator ?? false,
  );
  const [examiningTeamChairman, setExaminingTeamChairman] = useState<boolean>(data.examiningTeamChairman ?? false);
  const [techAdmin, setTechAdmin] = useState<boolean>(data.techAdmin ?? false);

  const [hasTitleFocus, setHasTitleFocus] = useState<boolean>(false);
  const [hasDescriptionFocus, setHasDescriptionFocus] = useState<boolean>(false);
  const [isDataChanged, setIsDataChanged] = useState<boolean>(true);
  const [isSpecialCaseTitleExist, setIsSpecialCaseTitleExist] = useState<boolean>(false);
  const [existedSpecialCaseTitle, setExistedSpecialCaseTitle] = useState<string>('');

  const isSpecialCaseTitleToLong = specialCaseTitle.length > TITLE_MAX_LENGTH;
  const isSpecialCaseDescriptionToLong = specialCaseDescription.length > DESCRIPTION_MAX_LENGTH;

  const handleSuccess = () => {
    openAlert('success');
    onClose();
  };

  const handleError = (error: { response: { data: SpecialCaseResult } }) => {
    if (error.response.data === SpecialCaseResult.TitleAlreadyExists) {
      setIsSpecialCaseTitleExist(true);
      setExistedSpecialCaseTitle(specialCaseTitle);
    }
    openAlert('error');
  };

  useEffect(() => {
    setIsSpecialCaseTitleExist(specialCaseTitle !== '' && specialCaseTitle === existedSpecialCaseTitle);
  }, [specialCaseTitle, existedSpecialCaseTitle]);

  const handleCreateSpecialCaseDefinition = () => {
    showLoader(true);
    createSpecialCasesDefinition(
      specialCaseTitle,
      specialCaseDescription,
      domesticCoordinator,
      examTaskGroupCoordinator,
      examiningTeamChairman,
      techAdmin,
    )
      .then(
        () => {
          handleSuccess();
        },
        (error) => {
          if (!navigator.onLine) {
            openAlert('error', 'W wyniku braku połączenia z internetem zapisywanie danych nie powiodło się.');
          } else {
            handleError(error);
          }
        },
      )
      .finally(() => {
        showLoader(false);
      });
  };

  const handleUpdateSpecialCaseDefinition = () => {
    showLoader(true);
    updateSpecialCasesDefinition(
      data.id,
      specialCaseTitle,
      specialCaseDescription,
      domesticCoordinator,
      examTaskGroupCoordinator,
      examiningTeamChairman,
      techAdmin,
    )
      .then(
        () => {
          handleSuccess();
        },
        (error) => {
          if (!navigator.onLine) {
            openAlert('error', 'W wyniku braku połączenia z internetem zapisywanie danych nie powiodło się.');
          } else {
            handleError(error);
          }
        },
      )
      .finally(() => {
        showLoader(false);
      });
  };

  const handleSpecialCaseTitleChange = (value: string) => {
    setSpecialCaseTitle(value);
  };

  const handleSpecialCaseDescriptionChange = (value: string) => {
    setSpecialCaseDescription(value);
  };

  useEffect(() => {
    if (workMode === WorkMode.ADD) {
      setIsDataChanged(
        specialCaseTitle !== '' &&
          specialCaseDescription !== '' &&
          (domesticCoordinator || examTaskGroupCoordinator || examiningTeamChairman || techAdmin),
      );
    } else {
      setIsDataChanged(
        (specialCaseTitle !== '' && specialCaseTitle !== data.title) ||
          (specialCaseDescription !== '' && specialCaseDescription !== data.description) ||
          domesticCoordinator !== data.domesticCoordinator ||
          examTaskGroupCoordinator !== data.examTaskGroupCoordinator ||
          examiningTeamChairman !== data.examiningTeamChairman ||
          techAdmin !== data.techAdmin,
      );
    }
  }, [
    specialCaseTitle,
    specialCaseDescription,
    domesticCoordinator,
    examTaskGroupCoordinator,
    examiningTeamChairman,
    techAdmin,
    data,
  ]);

  const handleCancel = () => {
    isDataChanged ? setIsLeavePromptOpen(true) : onCancel();
  };

  return (
    <>
      <ModalWithButtons
        xCloseButton={handleCancel}
        open={true}
        size="sm"
        title={title}
        autoHeight
        onCancel={handleCancel}
        confirmButtonLabel="Zapisz"
        onConfirm={() =>
          workMode === WorkMode.ADD ? handleCreateSpecialCaseDefinition() : handleUpdateSpecialCaseDefinition()
        }
        confirmButtonDisabled={
          !specialCaseTitle ||
          !specialCaseDescription ||
          isSpecialCaseTitleToLong ||
          isSpecialCaseDescriptionToLong ||
          isSpecialCaseTitleExist ||
          !isDataChanged ||
          (!domesticCoordinator && !examTaskGroupCoordinator && !examiningTeamChairman && !techAdmin)
        }
        buttonsContainerStyle={{ marginTop: '20px' }}
        handleClose={() => setIsLeavePromptOpen(true)}
      >
        <Grid container>
          <Grid item xs={12}>
            <Typography className={classes.subTitle}>Tytuł i opis*</Typography>
          </Grid>
          <Grid item xs={12} className={classes.item}>
            <TextField
              value={specialCaseTitle}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleSpecialCaseTitleChange(e.target.value)}
              label="Tytuł szczególnego przypadku"
              variant="outlined"
              fullWidth
              inputProps={{
                style: {
                  color: isSpecialCaseTitleToLong ? 'red' : 'black',
                },
              }}
              InputLabelProps={{
                shrink: true,
              }}
              onFocus={() => setHasTitleFocus(true)}
              onBlur={() => setHasTitleFocus(false)}
              error={isSpecialCaseTitleToLong || isSpecialCaseTitleExist}
              FormHelperTextProps={
                {
                  component: 'div',
                } as Partial<FormHelperTextProps<'p'>>
              }
              helperText={
                <ValidationRow
                  helperText={
                    isSpecialCaseTitleToLong
                      ? 'Wprowadzona nazwa jest za długa'
                      : isSpecialCaseTitleExist
                      ? 'Wprowadzona nazwa już istnieje w systemie'
                      : ''
                  }
                  limitsVisible={hasTitleFocus || isSpecialCaseTitleToLong}
                  valueLength={specialCaseTitle.length}
                  limit={TITLE_MAX_LENGTH}
                  withError={isSpecialCaseTitleToLong || isSpecialCaseTitleExist}
                />
              }
            />
          </Grid>
          <Grid item xs={12} className={classes.item}>
            <TextField
              value={specialCaseDescription}
              multiline
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleSpecialCaseDescriptionChange(e.target.value)}
              label="Opis"
              variant="outlined"
              fullWidth
              inputProps={{
                style: {
                  height: '100px',
                },
              }}
              InputLabelProps={{
                shrink: true,
              }}
              onFocus={() => setHasDescriptionFocus(true)}
              onBlur={() => setHasDescriptionFocus(false)}
              error={isSpecialCaseDescriptionToLong}
              FormHelperTextProps={
                {
                  component: 'div',
                } as Partial<FormHelperTextProps<'p'>>
              }
              helperText={
                <ValidationRow
                  helperText={isSpecialCaseDescriptionToLong ? 'Wprowadzony opis jest za długi' : ''}
                  limitsVisible={hasDescriptionFocus || isSpecialCaseDescriptionToLong}
                  valueLength={specialCaseDescription.length}
                  limit={DESCRIPTION_MAX_LENGTH}
                  withError={isSpecialCaseDescriptionToLong}
                />
              }
            />
          </Grid>
          <Grid item xs={12}>
            <Typography className={classes.subTitle}>Adresaci*</Typography>
          </Grid>

          <Grid item xs={12} className={classes.item}>
            <FormControlLabel
              control={
                <CheckboxOne
                  name={'KK'}
                  onChange={() => setDomesticCoordinator(!domesticCoordinator)}
                  checked={domesticCoordinator}
                />
              }
              label={'KK'}
            />
            <FormControlLabel
              control={
                <CheckboxOne
                  name={'KGZ'}
                  onChange={() => setExamTaskGroupCoordinator(!examTaskGroupCoordinator)}
                  checked={examTaskGroupCoordinator}
                />
              }
              label={'KGZ'}
            />
            <FormControlLabel
              control={
                <CheckboxOne
                  name={'PZE'}
                  onChange={() => setExaminingTeamChairman(!examiningTeamChairman)}
                  checked={examiningTeamChairman}
                />
              }
              label={'PZE'}
            />
            <FormControlLabel
              control={
                <CheckboxOne
                  name={'Administrator techniczny'}
                  onChange={() => setTechAdmin(!techAdmin)}
                  checked={techAdmin}
                />
              }
              label={'Administrator techniczny'}
            />
          </Grid>
        </Grid>
      </ModalWithButtons>
      <LeavePrompt isOpen={isLeavePromptOpen} onConfirm={onCancel} onCancel={() => setIsLeavePromptOpen(false)} />
    </>
  );
};
